import { connectSocket } from '#pplx/frontend/analytics/browser/socket.js';
import { EVENT_URL } from '#pplx/frontend/analytics/core.js';
import { redactTelemetrySearchParams, redactTelemetryUrl, } from '#pplx/frontend/common/redactAuthParams.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { getSessionIdCookie, getVisitorIdCookie, } from '#pplx/frontend/cookies/sessionCookieManager.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { getChosenLocale } from '#pplx/frontend/i18n/browser.js';
import { defaultTimezone } from '#pplx/frontend/i18n/core.js';
import { reportAction } from '#pplx/frontend/metrics/browser.js';
/**
 * Send an analytics event to server.
 * This intentionally doesn't return anything because it's meant to be fire-and-forget.
 * @param data analytics data
 */
function sendEvent(data) {
    if (typeof navigator.sendBeacon !== 'function')
        return false;
    return navigator.sendBeacon(EVENT_URL, new Blob([JSON.stringify(data)], {
        type: 'application/json; charset=UTF-8',
    }));
}
/**
 * This pulls some data from the browser that can be used to help identify anonymous users.
  Look at https://github.com/fingerprintjs/ for more info.
 */
function getDeviceInfo() {
    const deviceInfo = {};
    try {
        // Hardware Concurrency
        if (navigator.hardwareConcurrency) {
            deviceInfo.hardwareConcurrency = navigator.hardwareConcurrency;
        }
        // Architecture
        const f = new Float32Array(1);
        const u8 = new Uint8Array(f.buffer);
        f[0] = Infinity;
        f[0] = f[0] - f[0];
        const architecture = u8[3];
        if (architecture) {
            deviceInfo.architecture = architecture;
        }
        // Color Depth
        if (globalThis.screen.colorDepth) {
            deviceInfo.colorDepth = globalThis.screen.colorDepth;
        }
        // Screen Size
        if (globalThis.screen.width && globalThis.screen.height) {
            deviceInfo.screenSize = `${globalThis.screen.width}x${globalThis.screen.height}`;
        }
    }
    catch {
        // ignore
    }
    return deviceInfo;
}
const DEVICE_INFO = getDeviceInfo();
const defaultAnalyticsHostnameResolver = () => globalThis.location.hostname;
let analyticsHostnameResolver = defaultAnalyticsHostnameResolver;
/**
 * Override the `hostname` sent with every analytics event. Defaults to the
 * document hostname; platform shells (e.g. Perplexity Windows) inject their own
 * resolver to report a platform-specific identity without leaking that logic
 * into shared analytics. Pass `undefined` to restore the default.
 */
export function setAnalyticsHostnameResolver(resolver) {
    analyticsHostnameResolver = resolver ?? defaultAnalyticsHostnameResolver;
}
// Tailwind's default "md" breakpoint
const MOBILE_BREAKPOINT = 768;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;
/**
 * Check if the window size is within the mobile breakpoint.
 * Inlined to avoid circular dependencies with pplx/frontend/components.
 */
function isMobileScreenSize() {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}
/**
 * Detect the web platform type: comet_mobile, mweb, or web.
 */
function getWebPlatformType() {
    return isMobileScreenSize() ? 'mweb' : 'web';
}
export const _getEventData = (data = {}, user) => {
    const timestamp = Date.now();
    const webPlatform = getWebPlatformType();
    const eventData = {
        ...data,
        isStandaloneApp: false,
        timestamp,
        isBrowserExtension: false,
        timeZone: defaultTimezone,
        isPro: user?.subscription_status && user?.subscription_status !== 'none',
        userId: user?.id,
        orgId: user?.orgId,
        deviceInfo: DEVICE_INFO,
        web_platform: webPlatform,
    };
    // The referrer of a page reached from an /auth/ callback still carries the
    // callback's one-time credentials, both as the internal SPA path and as
    // document.referrer.
    if (eventData.internalReferrer) {
        eventData.internalReferrer = redactTelemetryUrl(eventData.internalReferrer);
    }
    const pathnameAndParams = globalThis.location.pathname +
        redactTelemetrySearchParams(globalThis.location.pathname, globalThis.location.search);
    const isArcBrowser = !!window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--arc-palette-title');
    const deviceInfo = getDeviceInfo();
    let referrer;
    if (eventData.internalReferrer) {
        referrer = eventData.internalReferrer;
    }
    else {
        referrer = redactTelemetryUrl(document.referrer);
    }
    if (globalThis.matchMedia('(display-mode: standalone)').matches ||
        globalThis.matchMedia('(display-mode: minimal-ui)').matches ||
        // @ts-expect-error this is non-standard and is only supported in iOS Safari
        globalThis.navigator?.standalone) {
        eventData.isStandaloneApp = true;
    }
    return {
        eventData,
        pathnameAndParams,
        isArcBrowser,
        deviceInfo,
        referrer,
    };
};
export function _toAnalyticsEventPayload(event) {
    const { name, data, user, source, overrides } = event;
    const { eventData, pathnameAndParams, deviceInfo, referrer, isArcBrowser } = _getEventData(data, user);
    const payload = {
        client_event_id: safeRandomUUID(),
        event_name: name,
        event_data: eventData,
        url: overrides?.url ?? pathnameAndParams,
        referrer: overrides?.referrer ?? referrer,
        language: getChosenLocale(),
        screen: getScreenSize(),
        hostname: overrides?.hostname ?? analyticsHostnameResolver(),
        device_info: deviceInfo,
        is_arc_browser: isArcBrowser,
        source,
        visitor_id: getVisitorIdCookie(),
        session_id: getSessionIdCookie(),
    };
    return payload;
}
// RUM action name for every mirrored analytics event. The original event name
// is carried in the `eventName` context field so it stays filterable in RUM
// without exploding the set of distinct action names.
const RUM_ANALYTICS_ACTION = 'analytics.event';
/**
 * Mirror an analytics event into Datadog RUM as an action so frontend
 * interactions become queryable in Datadog (not just Snowflake fct_events).
 *
 * Purely additive and fire-and-forget: the context is restricted to a few
 * low-cardinality, PII-safe scalars and any failure is swallowed so it can
 * never affect beacon/socket delivery.
 */
function reportEventToRum(payload) {
    try {
        const eventData = payload.event_data;
        reportAction(RUM_ANALYTICS_ACTION, {
            eventName: payload.event_name,
            source: payload.source,
            userId: eventData.userId,
            orgId: eventData.orgId,
            web_platform: eventData.web_platform,
        });
    }
    catch {
        // ignore — the RUM mirror must never affect analytics delivery
    }
}
export function trackEvent(event) {
    if (!BUILD_CAPABILITIES.productAnalytics)
        return;
    const payload = _toAnalyticsEventPayload(event);
    if (!payload) {
        return;
    }
    reportEventToRum(payload);
    if (!sendEvent(payload)) {
        reportAction('web.browser.analytics.send_event_failed');
        connectSocket().then((socket) => {
            socket.send(JSON.stringify(payload));
        });
    }
}
export function getScreenSize() {
    return `${globalThis.screen.width}x${globalThis.screen.height}`;
}
//# sourceMappingURL=browser.js.map